import classNames from 'classnames';
import {
    BRANDED_LIMIT_REACHED_WARNING_KEY,
    COLUMN_NAMES,
    DNS_ZONE,
    DOMAIN_LIMIT_WARNING_KEY,
    ONLY_ADMIN_WARNING_KEY,
    STATUS_SUB_VALUES,
    STATUS_TEXT_KEYS,
    STATUSES,
    STATUSES_COLOR_MAP,
    TABLE_KEYS,
    UPGRADE_PLAN_WARNING_KEY,
} from './consts';
import TableCellSubdomainInput from './TableComponents/TableCellSubdomainInput';
import TableCellSubdomainActions from './TableComponents/TableCellSubdomainActions';
import TableCellIcon from '../../../../components/widgets/TableCellIcon';
import css from './SubDomainLinkTable.css';
import TableCellDomainType from './TableComponents/TableCellDomainType';

const createTableFieldProps = fieldName => ({
    name: fieldName,
    cellValue: fieldName ? [fieldName] : null,
});

const createBackgroundTransformation = cssClass => ({
    propName: 'tdClassName',
    dataKey: 'isCreatingDomain',
    transformation: isCreating => (isCreating ? cssClass : ''),
});

const createDomainCell = (onChangeSubdomain, subdomainError) => ({
    ...createTableFieldProps(TABLE_KEYS.DOMAIN),
    displayName: COLUMN_NAMES.DOMAIN,
    cellComponent: TableCellSubdomainInput,
    cellProps: {
        onChange: onChangeSubdomain,
        error: subdomainError,
        tdClassName: css.tableCellContent,
    },
    cellValues: [
        createBackgroundTransformation(css.backgroundCreate),
        {
            propName: 'value',
            dataKey: 'subdomain',
        },
    ],
});

const createActionCell = (
    onArchiveClick,
    onFavouriteClick,
    onCreateClick,
    onCreateCancelClick,
    subDomainsPageData
) => ({
    ...createTableFieldProps(TABLE_KEYS.ACTIONS),
    displayName: COLUMN_NAMES.ACTIONS,
    cellComponent: TableCellSubdomainActions,
    headerWidth: 130,
    cellProps: {
        onFavouriteClick,
        onArchiveClick,
        onCreateClick,
        onCreateCancelClick,
        className: css.tableCellContent,
        subDomainsPageData,
    },
    cellValues: [createBackgroundTransformation(css.backgroundCreate)],
});

const createStatusCell = translate => ({
    ...createTableFieldProps(TABLE_KEYS.STATUS),
    displayName: COLUMN_NAMES.STATUS,
    cellComponent: TableCellIcon,
    cellValues: [
        {
            propName: 'value',
            dataKey: 'status',
        },
        {
            propName: 'subValue',
            dataKey: 'status',
            transformation: status => {
                const key = STATUS_SUB_VALUES[status];
                // Avoid calling translate with an undefined key
                return key ? translate(key) : '';
            },
        },
        createBackgroundTransformation(css.backgroundCreate),
    ],
    cellProps: {
        isBold: true,
        color: STATUSES_COLOR_MAP,
        text: {
            [STATUSES.LIVE]: translate(STATUS_TEXT_KEYS.LIVE),
            [STATUSES.EDIT]: translate(STATUS_TEXT_KEYS.EDIT),
            [STATUSES.UPGRADE]: translate(STATUS_TEXT_KEYS.UPGRADE),
            [STATUSES.LIMIT_REACHED]: translate(STATUS_TEXT_KEYS.LIMIT_REACHED),
            [STATUSES.PENDING_CERTIFICATE]: translate(STATUS_TEXT_KEYS.PENDING_CERTIFICATE),
            [STATUSES.PENDING_CNAME]: translate(STATUS_TEXT_KEYS.PENDING_CNAME),
        },
    },
});

const createDomainTypeCell = onDomainTypeChange => ({
    ...createTableFieldProps(TABLE_KEYS.DOMAIN_TYPE),
    displayName: COLUMN_NAMES.DOMAIN_TYPE,
    cellComponent: TableCellDomainType,
    headerWidth: 200,
    cellProps: {
        onChange: onDomainTypeChange,
    },
    cellValues: [
        {
            propName: 'className',
            dataKey: 'isCreatingDomain',
            transformation: isCreating => classNames(isCreating && css.backgroundCreateDomainCreating),
        },
        {
            propName: 'rightCellElement',
            dataKey: 'favourite',
            transformation: isFavourite => !isFavourite,
        },
    ],
});

export const getAlertMessage = (
    subdomains,
    domainsLimit,
    isPayingOrg,
    brandedDomainLimit,
    isBrandedDomain,
    isAdmin
) => {
    if (!isAdmin && isBrandedDomain) return ONLY_ADMIN_WARNING_KEY;
    if (isBrandedDomain && subdomains.filter(e => e.dns_zone !== DNS_ZONE).length >= brandedDomainLimit)
        return BRANDED_LIMIT_REACHED_WARNING_KEY;
    if (isPayingOrg && isBrandedDomain) return UPGRADE_PLAN_WARNING_KEY;
    if (subdomains.length >= domainsLimit) return DOMAIN_LIMIT_WARNING_KEY;
    return '';
};

export const getAlertButtonMessage = (isPayingOrg, isBrandedDomain) => {
    if (!isPayingOrg) return 'STATIC.PAGES.MANAGE_LINKS.SUB_DOMAIN.TABLE.CONTACT_SALES';
    if (isBrandedDomain) return 'STATIC.PAGES.MANAGE_LINKS.SUB_DOMAIN.TABLE.CONTACT_US';
    return '';
};

export const tableColumns = (
    onArchiveClicked,
    onFavouriteClicked,
    onCreateClick,
    onCancelCreateDomain,
    onChangeSubdomain,
    onChangeDomainType,
    validationError,
    isAgency,
    subDomainsPageData,
    translate
) => {
    const baseColumns = [
        createDomainTypeCell(onChangeDomainType),
        createDomainCell(onChangeSubdomain, validationError),
        createStatusCell(translate),
    ];

    if (!isAgency) {
        baseColumns.push(
            createActionCell(
                onArchiveClicked,
                onFavouriteClicked,
                onCreateClick,
                onCancelCreateDomain,
                subDomainsPageData
            )
        );
    }

    return baseColumns;
};
